import { Injectable } from '@angular/core';
import API, { graphqlOperation } from '@aws-amplify/api-graphql';

/**
 * A single stored KONTO assignment for a company.
 * All three identity fields (id, vatId, company) are optional — only
 * fields present on BOTH sides of a comparison are included in the score.
 */
export interface KontoEntry {
  id: string;       // supplier / customer ID number
  vatId: string;    // VAT number
  company: string;  // company name (used for fuzzy matching)
  konto: string;    // the assigned KONTO account code
  count: number;    // selection frequency — higher count = more trusted
}

/** @deprecated Legacy v1 format kept only for migration */
export type KontoFrequencyMap = { [identifier: string]: { [konto: string]: number } };

/** localStorage key for the v2 entry list */
const LS_KEY = 'kontoMappingStore_v2';
/** localStorage key used by the legacy v1 implementation */
const LS_KEY_LEGACY = 'kontoFrequencyMap';

/**
 * Minimum similarity score required for a stored entry to be auto-applied.
 * Score is the average field-level similarity across all fields that are
 * non-empty on BOTH the stored entry and the current row.
 */
const AUTO_APPLY_THRESHOLD = 0.8;
/**
 * Weight for company name in similarity scoring — company is prioritized
 * over ID/vatID because it's more reliably extracted from documents.
 */
const COMPANY_WEIGHT = 3;
/**
 * When company similarity exceeds this threshold, the overall auto-apply
 * threshold is lowered to allow more matches when company name is a strong signal.
 */
const COMPANY_SIMILARITY_HIGH_THRESHOLD = 0.9;
/**
 * Reduced threshold to use when company similarity is very high.
 */
const AUTO_APPLY_THRESHOLD_WITH_HIGH_COMPANY = 0.5;

@Injectable({ providedIn: 'root' })
export class KontoMappingService {
  private entries: KontoEntry[] = [];

  // Storage locations; overridden by subclasses that persist a different
  // code mapping (e.g. SchemaMappingService) while sharing the matching logic.
  protected readonly lsKey: string = LS_KEY;
  protected readonly lsKeyLegacy: string | null = LS_KEY_LEGACY;
  protected readonly dbField: string = 'kontoMappings';

  // ─── Load ────────────────────────────────────────────────────────────────

  /**
   * Loads entries for the given client.
   * Tries the DB first; falls back to localStorage (and migrates legacy data).
   */
  async load(clientID: string): Promise<void> {
    try {
      const dbEntries = await this.fetchFromDB(clientID);
      if (dbEntries && dbEntries.length > 0) {
        this.entries = dbEntries;
        this.persistToLocalStorage();
        return;
      }
    } catch (err) {
      console.warn('KontoMappingService: DB load failed, using localStorage.', err);
    }
    this.loadFromLocalStorage();
  }

  // ─── Query ───────────────────────────────────────────────────────────────

  /**
   * Returns the best-matching KONTO for the given row values using fuzzy
   * multi-field matching.
   *
   * Scoring rules (per field):
   *  - ID / vatId: 1.0 if exact match (case-insensitive, trimmed), 0 otherwise
   *  - company: Dice-coefficient bigram similarity (0–1), weighted 3x
   *
   * Only fields where BOTH the stored entry and the current row have a
   * non-empty value contribute to the denominator.  If the weighted average
   * is >= AUTO_APPLY_THRESHOLD (0.8), the entry is considered a confident
   * match and its konto is returned.
   *
   * When company similarity is very high (>0.9), the threshold is lowered
   * to 0.5 to allow more matches since company name is the most reliable field.
   *
   * When multiple entries reach the threshold the one with the highest
   * score is preferred; ties are broken by selection count.
   */
  getSuggestionForRow(id: string, vatId: string, company: string = ''): string {
    if (!id && !vatId && !company) return '';

    let bestScore = 0;
    let bestEntry: KontoEntry | null = null;
    let bestCompanySimilarity = 0;

    for (const entry of this.entries) {
      const { score, companySimilarity } = this.computeMatchScore(entry, id, vatId, company);
      if (
        score > bestScore ||
        (score === bestScore && bestEntry && entry.count > bestEntry.count)
      ) {
        bestScore = score;
        bestEntry = entry;
        bestCompanySimilarity = companySimilarity;
      }
    }

    // Use lowered threshold when company similarity is very high
    const threshold = bestCompanySimilarity > COMPANY_SIMILARITY_HIGH_THRESHOLD
      ? AUTO_APPLY_THRESHOLD_WITH_HIGH_COMPANY
      : AUTO_APPLY_THRESHOLD;

    if (bestScore >= threshold && bestEntry) {
      return bestEntry.konto;
    }

    return '';
  }

  // ─── Record ──────────────────────────────────────────────────────────────

  /**
   * Records a KONTO selection for the given row identity fields.
   * Increments the count on an existing matching entry or creates a new one.
   * Persists to localStorage immediately and saves to DB asynchronously.
   */
  async record(
    id: string,
    vatId: string,
    company: string,
    konto: string,
    clientID: string
  ): Promise<void> {
    if (!konto) return;
    if (!id && !vatId && !company) return;

    const normId  = (id    || '').trim().toLowerCase();
    const normVat = (vatId || '').trim().toLowerCase();

    // Find an existing entry for the same identity + konto combination
    const existing = this.entries.find(
      (e) =>
        e.konto === konto &&
        (e.id    || '').trim().toLowerCase() === normId &&
        (e.vatId || '').trim().toLowerCase() === normVat
    );

    if (existing) {
      existing.count += 1;
      // Enrich with company name if it was not stored before
      if (company && !existing.company) {
        existing.company = company;
      }
    } else {
      this.entries.push({
        id:      id      || '',
        vatId:   vatId   || '',
        company: company || '',
        konto,
        count: 1,
      });
    }

    this.persistToLocalStorage();
    try {
      await this.saveToDB(clientID, this.entries);
    } catch (err) {
      console.warn('KontoMappingService: DB save failed, data is in localStorage.', err);
    }
  }

  // ─── Fuzzy matching ───────────────────────────────────────────────────────

  /**
   * Computes a similarity score (0–1) between a stored entry and the current
   * row's identity values.  Only fields that are non-empty on both sides are
   * counted.  Returns { score, companySimilarity }.
   */
  private computeMatchScore(
    entry: KontoEntry,
    id: string,
    vatId: string,
    company: string
  ): { score: number; companySimilarity: number } {
    let totalWeight = 0;
    let matchScore  = 0;
    let companySimilarity = 0;

    const entryCompany = (entry.company || '').trim().toLowerCase();
    const rowCompany   = (company       || '').trim().toLowerCase();
    if (entryCompany && rowCompany) {
      companySimilarity = this.stringSimilarity(entryCompany, rowCompany);
      totalWeight += COMPANY_WEIGHT;
      matchScore  += companySimilarity * COMPANY_WEIGHT;
    }

    const entryId = (entry.id || '').trim().toLowerCase();
    const rowId   = (id       || '').trim().toLowerCase();
    if (entryId && rowId) {
      totalWeight += 1;
      matchScore  += entryId === rowId ? 1 : 0;
    }

    const entryVat = (entry.vatId || '').trim().toLowerCase();
    const rowVat   = (vatId       || '').trim().toLowerCase();
    if (entryVat && rowVat) {
      totalWeight += 1;
      matchScore  += entryVat === rowVat ? 1 : 0;
    }

    if (totalWeight === 0) return { score: 0, companySimilarity: 0 };
    return { score: matchScore / totalWeight, companySimilarity };
  }

  /**
   * Dice-coefficient similarity using character bigrams.
   * Returns 1 for identical strings, 0 for no shared bigrams.
   */
  private stringSimilarity(a: string, b: string): number {
    if (a === b) return 1;
    if (a.length < 2 || b.length < 2) return a === b ? 1 : 0;

    const bigrams = (s: string): Map<string, number> => {
      const m = new Map<string, number>();
      for (let i = 0; i < s.length - 1; i++) {
        const bg = s.slice(i, i + 2);
        m.set(bg, (m.get(bg) || 0) + 1);
      }
      return m;
    };

    const aBg = bigrams(a);
    const bBg = bigrams(b);
    let intersection = 0;
    aBg.forEach((cnt, bg) => {
      intersection += Math.min(cnt, bBg.get(bg) || 0);
    });

    const total = (a.length - 1) + (b.length - 1);
    return total > 0 ? (2 * intersection) / total : 0;
  }

  // ─── localStorage ────────────────────────────────────────────────────────

  private loadFromLocalStorage(): void {
    try {
      const raw = localStorage.getItem(this.lsKey);
      if (raw) {
        this.entries = JSON.parse(raw) as KontoEntry[];
        return;
      }
      // Fall back to legacy v1 key and migrate
      const legacyRaw = this.lsKeyLegacy ? localStorage.getItem(this.lsKeyLegacy) : null;
      if (legacyRaw) {
        this.entries = this.migrateFromLegacy(JSON.parse(legacyRaw));
        this.persistToLocalStorage();
      }
    } catch {
      this.entries = [];
    }
  }

  private persistToLocalStorage(): void {
    try {
      localStorage.setItem(this.lsKey, JSON.stringify(this.entries));
    } catch {}
  }

  /**
   * Converts the legacy KontoFrequencyMap format to KontoEntry[].
   * Handles both the original flat { id: 'konto' } format and the
   * frequency { id: { konto: count } } format.
   */
  private migrateFromLegacy(raw: any): KontoEntry[] {
    const entries: KontoEntry[] = [];
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return entries;

    for (const identifier of Object.keys(raw)) {
      const val = raw[identifier];
      if (typeof val === 'string') {
        entries.push({ id: identifier, vatId: '', company: '', konto: val, count: 1 });
      } else if (typeof val === 'object' && val !== null) {
        for (const [konto, count] of Object.entries(val)) {
          entries.push({ id: identifier, vatId: '', company: '', konto, count: count as number });
        }
      }
    }
    return entries;
  }

  // ─── DB (kontoMappings / schemaMappings field on Client record) ──────────

  private async fetchFromDB(clientID: string): Promise<KontoEntry[] | null> {
    const statement = `query GetClientCodeMappings($id: ID!) {
      getClient(id: $id) {
        ${this.dbField}
      }
    }`;
    const res = (await API.graphql(graphqlOperation(statement, { id: clientID }))) as any;
    const raw = res?.data?.getClient?.[this.dbField];
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // New format is an array; old format is an object — migrate transparently
    if (Array.isArray(parsed)) {
      return parsed as KontoEntry[];
    }
    return this.migrateFromLegacy(parsed);
  }

  private async saveToDB(clientID: string, entries: KontoEntry[]): Promise<void> {
    const statement = `mutation SaveCodeMappings($input: UpdateClientInput!) {
      updateClient(input: $input) {
        id
        ${this.dbField}
      }
    }`;
    await API.graphql(
      graphqlOperation(statement, {
        input: { id: clientID, [this.dbField]: JSON.stringify(entries) },
      })
    );
  }
}

/**
 * Same learning/suggestion logic as KontoMappingService, but for the `schema`
 * template field: remembers which account schema the user picked for a
 * supplier and suggests it next time. Stored in Client.schemaMappings.
 */
@Injectable({ providedIn: 'root' })
export class SchemaMappingService extends KontoMappingService {
  protected readonly lsKey = 'schemaMappingStore_v1';
  protected readonly lsKeyLegacy = null;
  protected readonly dbField = 'schemaMappings';
}
